import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuthStore } from '../store/authStore';
import {
  C, StagePill, SourceTag, Drawer, ErrorBox, WalkInDrawer, CallFormDrawer, TaskDrawer, CompleteTaskModal, OwnerSelect, ProductPicker,
  useCallReturn, fmtDateTime, fmtDate, fmtINR, leadName, errOf, inputStyle, labelStyle, btn, toLocalInput, fromLocalInput
} from '../components/leads/LeadForms';
import { ExportMenu } from '../components/leads/LeadTools';
import LeadAnalytics from '../components/leads/LeadAnalytics';
import LeadReview from '../components/leads/LeadReview';
import LeadSettings from '../components/leads/LeadSettings';

/**
 * Leads tab (Lead Management on POS — Phase 1, Nov 2026).
 * /leads       → list (All / Open / Store Follow-up / Closed) + My Tasks
 * /leads/:id   → lead detail (Activity History · Tasks · Lead Details · Remarks, Notes rail)
 * /leads/analytics → Lead analytics + Export (every role, own scope)
 * /leads/review    → ABM / HO review queue (order matches at another store, move requests) — managers
 * /leads/settings  → stages / call remarks / task types, pincode map, LSQ import, weekly email — HO
 * Store logins: scoped to the store picked in the Location switcher (header).
 * Managers / super admin: own store dropdown like the Region EOD report — "All Stores" (default) or one store.
 */

const useStore = () => {
  const [storeId, setStoreId] = useState(localStorage.getItem('selectedLocation') || '');
  useEffect(() => {
    const on = (e) => setStoreId(e.detail || localStorage.getItem('selectedLocation') || '');
    window.addEventListener('locationchange', on);
    return () => window.removeEventListener('locationchange', on);
  }, []);
  return storeId;
};
const notify = () => window.dispatchEvent(new Event('leads-changed')); // refreshes the nav badge

const STORE_KEY = 'leads-store'; // manager's pick on this page, kept for the browser tab session

export default function Leads() {
  const { id } = useParams();
  const { user } = useAuthStore();
  const roles = user?.roles || [];
  const managerView = (roles.includes('manager') && !roles.includes('pos')) || roles.includes('super_admin'); // same as App.js
  const headerStore = useStore();
  const [mgrStore, setMgrStore] = useState(() => { try { return sessionStorage.getItem(STORE_KEY) || 'all'; } catch { return 'all'; } });
  const [stores, setStores] = useState([]);
  const [meta, setMeta] = useState(null);
  useEffect(() => { api.get('/leads/meta').then(r => setMeta(r.data)).catch(() => setMeta({ stages: [], dispositions: [], taskTypes: [], sources: [], perms: {} })); }, []);
  useEffect(() => { if (managerView) api.get('/leads/stores').then(r => setStores(r.data.stores || [])).catch(() => setStores([])); }, [managerView]);
  const pickStore = (v) => { setMgrStore(v); try { sessionStorage.setItem(STORE_KEY, v); } catch { /* storage blocked */ } };
  useEffect(() => { // "Open CX queue" in the HO banner
    const on = () => setMgrStore('CX');
    window.addEventListener('leads-store-cx', on);
    return () => window.removeEventListener('leads-store-cx', on);
  }, []);
  if (!meta) return <div style={{ padding: 40, textAlign: 'center', color: C.faint }}>Loading…</div>;
  const scopeStore = managerView ? mgrStore : headerStore;
  if (id === 'analytics') return <LeadAnalytics storeId={scopeStore} stores={managerView ? stores : null} onStoreChange={pickStore} />;
  if (id === 'review') return meta.perms?.canReview ? <LeadReview storeId={mgrStore} stores={stores} onStoreChange={pickStore} /> : <NoAccess />;
  if (id === 'settings') return meta.perms?.canEditSettings ? <LeadSettings stores={stores} /> : <NoAccess />;
  if (id) return <LeadDetail id={id} meta={meta} />;
  return managerView
    ? <LeadsList storeId={mgrStore} meta={meta} stores={stores} onStoreChange={pickStore} />
    : <LeadsList storeId={headerStore} meta={meta} />;
}

function NoAccess() {
  const navigate = useNavigate();
  return <div style={{ maxWidth: 600, margin: '40px auto', textAlign: 'center', color: C.muted }}>This page is for managers / HO.<div style={{ marginTop: 12 }}><button onClick={() => navigate('/leads')} style={btn('secondary')}>‹ Leads</button></div></div>;
}
// Phones / small tablets: the list turns into cards (PRD "Mobile layout").
const useNarrow = () => {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 720);
  useEffect(() => { const on = () => setNarrow(window.innerWidth < 720); window.addEventListener('resize', on); return () => window.removeEventListener('resize', on); }, []);
  return narrow;
};
// Filters handed over in the URL (Lead analytics → tap a stage bar).
const urlFilters = () => {
  try {
    const u = new URLSearchParams(window.location.search);
    return { stages: (u.get('stages') || '').split(',').filter(Boolean), source: u.get('sources') || '', created: u.get('created') || '', from: u.get('from') || '', to: u.get('to') || '' };
  } catch { return { stages: [], source: '', created: '', from: '', to: '' }; }
};

const stageLabel = (meta, code) => (meta.stages.find(s => s.code === code) || {}).label || code;
const taskLabel = (meta, code) => (meta.taskTypes.find(t => t.code === code) || {}).label || code;
const card = { background: '#fff', border: `1px solid ${C.border}`, borderRadius: '12px' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: '11px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', fontSize: '13px', color: C.text, borderBottom: '1px solid #f4ede4', verticalAlign: 'middle' };
const pill = (on) => ({ padding: '6px 14px', borderRadius: '18px', border: `1px solid ${on ? C.brand : C.border}`, background: on ? C.brand : '#fff', color: on ? '#fff' : C.muted, fontWeight: 700, fontSize: '12.5px', cursor: 'pointer', whiteSpace: 'nowrap' });
const Dot = ({ sla }) => sla ? <span title={sla === 'red' ? 'Overdue task' : 'New for over 2 hours'} style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: sla === 'red' ? '#dc2626' : '#f59e0b', marginRight: 6 }} /> : <span style={{ display: 'inline-block', width: 8, marginRight: 6 }} />;

// ── List ─────────────────────────────────────────────────────────────────────
function LeadsList({ storeId, meta, stores, onStoreChange }) {
  const navigate = useNavigate();
  const allStores = storeId === 'all';
  const init = useMemo(urlFilters, []);
  const narrow = useNarrow();
  const [view, setView] = useState('all'); // all | open | followup | closed | tasks
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [stages, setStages] = useState(init.stages);
  const [source, setSource] = useState(init.source);
  const [owner, setOwner] = useState('');
  const [created, setCreated] = useState(init.created);
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [reviewCount, setReviewCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [walkIn, setWalkIn] = useState(false);
  const [toast, setToast] = useState('');
  const [stageOpen, setStageOpen] = useState(false);
  const [callFor, setCallFor] = useState(null); // lead whose Phone Call Form is open (after tapping its number)
  const markCall = useCallReturn(useCallback((p) => {
    api.get(`/leads/${p.leadId}`).then(r => setCallFor(r.data.lead)).catch(() => {});
  }, []));

  useEffect(() => { const t = setTimeout(() => setQDebounced(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [view, qDebounced, stages, source, owner, created, from, to, pageSize, storeId]);

  const load = useCallback(() => {
    if (!storeId || view === 'tasks') return;
    setError('');
    api.get('/leads', { params: { storeId, view, q: qDebounced, stages: stages.join(','), sources: source, owner, created, from, to, page, pageSize } })
      .then(r => setData(r.data))
      .catch(e => { setData(null); setError(errOf(e, 'Could not load leads')); });
  }, [storeId, view, qDebounced, stages, source, owner, created, from, to, page, pageSize]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { // a Sage / website / Pop-ins lead just arrived (LeadReminders) → refresh the list
    window.addEventListener('leads-arrived', load);
    return () => window.removeEventListener('leads-arrived', load);
  }, [load]);
  useEffect(() => {
    if (!meta.perms?.canReview) return;
    api.get('/leads/review/count', { params: { storeId: storeId || 'all' } }).then(r => setReviewCount(r.data.count || 0)).catch(() => setReviewCount(0));
  }, [meta.perms, storeId]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };
  const counts = data?.counts || {};
  const exportParams = { storeId, view: view === 'tasks' ? 'all' : view, q: qDebounced, stages: stages.join(','), sources: source, owner, created, from, to };
  const tabs = [['all', 'All Leads', counts.all], ['open', 'Open Leads', counts.open], ['followup', 'Store Follow-up', counts.followup], ['closed', 'Closed Leads', counts.closed], ['tasks', 'My Tasks']];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 20, color: C.text, fontWeight: 800 }}>Leads</h2>
        {stores && (
          <select value={storeId} onChange={e => onStoreChange(e.target.value)} title="Filter to a specific store" style={{ ...inputStyle, width: 'auto', maxWidth: 240, fontWeight: 600 }}>
            <option value="all">All Stores</option>
            {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => navigate('/leads/analytics')} disabled={!storeId} style={btn('secondary', !storeId)}>📊 Analytics</button>
        {storeId && <ExportMenu params={exportParams} period={created === 'custom' && from && to ? { from, to } : null} onError={(m) => setError(m)} />}
        {meta.perms?.canReview && <button onClick={() => navigate('/leads/review')} style={btn('secondary')}>Review{reviewCount ? ` (${reviewCount})` : ''}</button>}
        {meta.perms?.canEditSettings && <button onClick={() => navigate('/leads/settings')} style={btn('secondary')}>⚙️ Settings</button>}
        <button onClick={() => setWalkIn(true)} disabled={!storeId || storeId === 'CX'} style={btn('primary', !storeId || storeId === 'CX')}>＋ Add Walk-in Lead</button>
      </div>
      {toast && <div style={{ marginBottom: 12, padding: '9px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>{toast}</div>}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {tabs.map(([k, l, n]) => <button key={k} onClick={() => setView(k)} style={pill(view === k)}>{l}{n != null ? ` (${n})` : ''}</button>)}
      </div>

      {view === 'tasks' ? <MyTasks storeId={storeId} meta={meta} /> : (
        <div style={card}>
          <div style={{ display: 'flex', gap: 8, padding: 12, flexWrap: 'wrap', alignItems: 'center', borderBottom: `1px solid ${C.border}` }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, phone or lead ID" style={{ ...inputStyle, width: 240 }} />
            <div style={{ position: 'relative' }}>
              <button onClick={() => setStageOpen(o => !o)} style={{ ...btn('secondary'), fontWeight: 600 }}>Stage{stages.length ? ` (${stages.length})` : ''} ▾</button>
              {stageOpen && (
                <div onMouseLeave={() => setStageOpen(false)} style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, marginTop: 4, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: 6, width: 230, boxShadow: '0 8px 20px rgba(0,0,0,0.08)' }}>
                  {meta.stages.map(s => (
                    <label key={s.code} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 6px', fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={stages.includes(s.code)} onChange={() => setStages(v => v.includes(s.code) ? v.filter(x => x !== s.code) : [...v, s.code])} style={{ accentColor: C.brand }} />{s.label}
                    </label>
                  ))}
                  {stages.length > 0 && <button onClick={() => setStages([])} style={{ ...btn('secondary'), width: '100%', marginTop: 4, padding: '5px' }}>Clear</button>}
                </div>
              )}
            </div>
            <select value={source} onChange={e => setSource(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
              <option value="">All sources</option>
              {meta.sources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div style={{ width: 190 }}><OwnerSelect storeId={storeId} value={owner} onChange={setOwner} allowBlank={false} /></div>
            {owner && <button onClick={() => setOwner('')} style={{ ...btn('secondary'), padding: '6px 10px' }}>All owners</button>}
            <select value={created} onChange={e => setCreated(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
              <option value="">Created: any time</option>
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="custom">Custom…</option>
            </select>
            {created === 'custom' && (<>
              <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...inputStyle, width: 'auto' }} />
              <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ ...inputStyle, width: 'auto' }} />
            </>)}
          </div>
          <ErrorBox text={error} />
          {narrow && (data?.leads || []).map(l => (
            <div key={l.id} onClick={() => navigate(`/leads/${l.id}`)} style={{ padding: '10px 12px', borderBottom: '1px solid #f4ede4', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Dot sla={l.sla} /><b style={{ flex: 1, fontSize: 14 }}>{leadName(l)}</b><StagePill stage={l.stage} label={stageLabel(meta, l.stage)} /></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontSize: 12, color: C.muted, flexWrap: 'wrap' }}>
                <SourceTag source={l.source} />{allStores && <span>{l.store_name}</span>}
                <span style={{ color: l.overdue ? '#b91c1c' : C.muted, fontWeight: l.overdue ? 700 : 400 }}>{l.next_task_at ? `Next: ${fmtDateTime(l.next_task_at)}` : 'No open task'}</span>
                <div style={{ flex: 1 }} />
                <a href={`tel:+91${l.phone}`} onClick={(e) => { e.stopPropagation(); markCall({ leadId: l.id }); }} style={{ ...btn('primary'), padding: '6px 12px', textDecoration: 'none' }}>📞 Call</a>
              </div>
            </div>
          ))}
          <div style={{ overflowX: 'auto', display: narrow ? 'none' : 'block' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead><tr>
                <th style={th}>Lead</th>{allStores && <th style={th}>Store</th>}<th style={th}>Phone</th><th style={th}>Stage</th><th style={th}>Source</th>
                <th style={th}>Next task</th><th style={th}>Owner</th><th style={th}>Order value</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {(data?.leads || []).map(l => (
                  <tr key={l.id} onClick={() => navigate(`/leads/${l.id}`)} style={{ cursor: 'pointer' }}>
                    <td style={td}><Dot sla={l.sla} /><b>{leadName(l)}</b><div style={{ fontSize: 11, color: C.faint, marginLeft: 14 }}>{l.lead_code} · {fmtDate(l.created_at)}</div></td>
                    {allStores && <td style={{ ...td, fontSize: 12 }}>{l.store_name || l.store_id}</td>}
                    <td style={td} onClick={e => e.stopPropagation()}>
                      <a href={`tel:+91${l.phone}`} onClick={() => markCall({ leadId: l.id })} style={{ color: C.brand, fontWeight: 600, textDecoration: 'none' }}>+91 {l.phone}</a>
                      {l.dnd_call ? <span title="Do not call" style={{ marginLeft: 6, fontSize: 10, color: '#b91c1c', fontWeight: 700 }}>DND</span> : null}
                    </td>
                    <td style={td}><StagePill stage={l.stage} label={stageLabel(meta, l.stage)} /></td>
                    <td style={td}><SourceTag source={l.source} />{l.visit_day && l.source !== 'Store walk-in' ? <div style={{ fontSize: 11, color: C.faint, marginTop: 3 }}>Visit {fmtDate(l.visit_day)}</div> : null}</td>
                    <td style={{ ...td, fontSize: 12, color: l.overdue ? '#b91c1c' : C.text, fontWeight: l.overdue ? 700 : 400 }}>{l.next_task_at ? <>{fmtDateTime(l.next_task_at)}<div style={{ fontSize: 11, color: C.faint, fontWeight: 400 }}>{taskLabel(meta, l.next_task_type)}</div></> : '—'}</td>
                    <td style={{ ...td, fontSize: 12 }}>{l.owner_staff || <span style={{ color: C.faint }}>Unassigned</span>}</td>
                    <td style={td}>{l.order_value != null ? fmtINR(l.order_value) : <span style={{ color: C.faint, fontSize: 12 }}>{l.expected_order_value != null ? `exp. ${fmtINR(l.expected_order_value)}` : '—'}</span>}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}><span style={{ color: C.brand, fontWeight: 700, fontSize: 12 }}>Open ›</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data && data.leads.length === 0 && (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: C.faint, fontSize: 13 }}>
                {counts.all === 0 && !qDebounced && !stages.length && !source && !owner && !created
                  ? 'No leads yet — add a walk-in or wait for Sage to route one to you.'
                  : 'No leads match these filters.'}
              </div>
            )}
            {!data && !error && <div style={{ padding: 30, textAlign: 'center', color: C.faint }}>Loading…</div>}
          </div>
          {data && data.total > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', fontSize: 12.5, color: C.muted, flexWrap: 'wrap' }}>
              <span>Show</span>
              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ ...inputStyle, width: 'auto', padding: '4px 8px' }}>
                {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>{(data.page - 1) * data.pageSize + 1}–{Math.min(data.page * data.pageSize, data.total)} of {data.total}</span>
              <div style={{ flex: 1 }} />
              <button disabled={data.page <= 1} onClick={() => setPage(p => p - 1)} style={btn('secondary', data.page <= 1)}>‹ Prev</button>
              <button disabled={data.page >= data.totalPages} onClick={() => setPage(p => p + 1)} style={btn('secondary', data.page >= data.totalPages)}>Next ›</button>
            </div>
          )}
        </div>
      )}

      {walkIn && (
        <WalkInDrawer storeId={allStores ? '' : storeId} stores={allStores ? stores : null} me={meta.username} onClose={() => setWalkIn(false)} onOpenLead={(lid) => navigate(`/leads/${lid}`)}
          onSaved={(r) => { setWalkIn(false); flash(r.reEnquiry ? `Re-enquiry logged on ${r.lead.lead_code}` : `Lead ${r.lead.lead_code} added`); load(); notify(); }} />
      )}
      {callFor && <CallFormDrawer lead={callFor} meta={meta} onClose={() => setCallFor(null)} onSaved={(r) => { setCallFor(null); flash(r.message || 'Call saved'); load(); notify(); }} />}
    </div>
  );
}

// ── My Tasks / Today ─────────────────────────────────────────────────────────
function MyTasks({ storeId, meta }) {
  const navigate = useNavigate();
  const [mine, setMine] = useState(false);
  const [data, setData] = useState(null);
  const [complete, setComplete] = useState(null);
  const [callFor, setCallFor] = useState(null); // { lead, taskId }
  const load = useCallback(() => {
    if (!storeId) return;
    api.get('/leads/tasks', { params: { storeId, mine: mine ? 1 : 0 } }).then(r => setData(r.data)).catch(() => setData({ overdue: [], today: [], upcoming: [] }));
  }, [storeId, mine]);
  useEffect(() => { load(); }, [load]);
  const openCall = async (t) => {
    try { const r = await api.get(`/leads/${t.lead_id}`); setCallFor({ lead: r.data.lead, taskId: t.id }); } catch (e) { /* lead gone */ }
  };
  const markCall = useCallReturn(useCallback((p) => openCall(p.task), [])); // eslint-disable-line react-hooks/exhaustive-deps
  const Group = ({ title, rows, color }) => (
    <div style={{ ...card, marginBottom: 12 }}>
      <div style={{ padding: '10px 14px', fontSize: 13, fontWeight: 800, color, borderBottom: `1px solid ${C.border}` }}>{title} ({rows.length})</div>
      {rows.length === 0 && <div style={{ padding: '14px', fontSize: 12.5, color: C.faint }}>Nothing here.</div>}
      {rows.map(t => (
        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid #f4ede4', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220, cursor: 'pointer' }} onClick={() => navigate(`/leads/${t.lead_id}`)}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{t.subject}</div>
            <div style={{ fontSize: 11.5, color: C.muted }}>{fmtDateTime(t.scheduled_at)} · {taskLabel(meta, t.task_type)} · {t.lead_code}{storeId === 'all' && t.store_name ? ` · ${t.store_name}` : ''}{t.owner_staff ? ` · ${t.owner_staff}` : ''}</div>
          </div>
          <a href={`tel:+91${t.phone}`} onClick={() => markCall({ task: t })} style={{ ...btn('secondary'), textDecoration: 'none', padding: '6px 10px' }}>📞 Call</a>
          <button onClick={() => openCall(t)} style={{ ...btn('secondary'), padding: '6px 10px' }}>Log call</button>
          <button onClick={() => setComplete(t)} style={{ ...btn('primary'), padding: '6px 10px' }}>✓ Complete</button>
        </div>
      ))}
    </div>
  );
  return (
    <div>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: C.text, marginBottom: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={mine} onChange={e => setMine(e.target.checked)} style={{ accentColor: C.brand }} />Only tasks I own ({meta.username})
      </label>
      {!data ? <div style={{ padding: 30, textAlign: 'center', color: C.faint }}>Loading…</div> : (<>
        <Group title="Overdue" rows={data.overdue} color="#b91c1c" />
        <Group title="Today" rows={data.today} color={C.brand} />
        <Group title="Upcoming" rows={data.upcoming} color={C.muted} />
      </>)}
      {complete && <CompleteTaskModal task={complete} onClose={() => setComplete(null)} onSaved={() => { setComplete(null); load(); notify(); }} />}
      {callFor && <CallFormDrawer lead={callFor.lead} meta={meta} completeTaskId={callFor.taskId} onClose={() => setCallFor(null)} onSaved={() => { setCallFor(null); load(); notify(); }} />}
    </div>
  );
}

// ── Lead detail ──────────────────────────────────────────────────────────────
const ACT = {
  LEAD_CREATED: ['🧲', 'Lead added'], LEAD_RECEIVED: ['📥', 'Lead received'], RE_ENQUIRY: ['🔁', 'Re-enquired'], CALL_LOGGED: ['📞', 'Call logged'], STAGE_CHANGED: ['🏷️', 'Stage changed'],
  NOTE_ADDED: ['📝', 'Note'], TASK_CREATED: ['📅', 'Task created'], TASK_COMPLETED: ['☑️', 'Task completed'], TASK_CANCELLED: ['🚫', 'Task cancelled'],
  LEAD_UPDATED: ['✏️', 'Details edited'], LEAD_REASSIGNED: ['↪️', 'Reassigned'], ORDER_LINKED: ['🛒', 'Order linked'], LEAD_DELETED: ['🗑️', 'Deleted'],
  LEAD_IMPORTED: ['📦', 'Imported from LSQ'], REASSIGN_REQUESTED: ['🙋', 'Move requested'], ORDER_MATCH_FLAGGED: ['🔎', 'Order sent for review'], REVIEW_DISMISSED: ['✖️', 'Review dismissed']
};
const OUTCOMES = { done: 'Done', no_show: 'Customer no-show', rescheduled: 'Rescheduled' };

function LeadDetail({ id, meta }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('activity');
  const [drawer, setDrawer] = useState(null); // call | task | stage | link | reassign
  const [callTaskId, setCallTaskId] = useState(null);
  const [complete, setComplete] = useState(null);
  const [toast, setToast] = useState('');
  const load = useCallback(() => {
    api.get(`/leads/${id}`).then(r => { setData(r.data); setError(''); }).catch(e => setError(errOf(e, 'Could not load the lead')));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const markCall = useCallReturn(useCallback(() => { setCallTaskId(null); setDrawer('call'); }, []));
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };
  const done = (msg) => { setDrawer(null); setCallTaskId(null); load(); notify(); if (msg) flash(msg); };

  if (error) return <div style={{ maxWidth: 900, margin: '0 auto' }}><button onClick={() => navigate('/leads')} style={btn('secondary')}>‹ Back to leads</button><ErrorBox text={error} /></div>;
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: C.faint }}>Loading…</div>;
  const { lead, activities, tasks, notes, perms } = data;
  const openTasks = tasks.filter(t => t.status === 'open');
  const row = (k, v) => <div style={{ display: 'flex', fontSize: 12.5, padding: '4px 0' }}><span style={{ width: 92, color: C.faint }}>{k}</span><span style={{ flex: 1, color: C.text, fontWeight: 600 }}>{v || '—'}</span></div>;
  const quick = { ...btn('secondary'), padding: '6px 10px', fontSize: 12 };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <button onClick={() => navigate('/leads')} style={btn('secondary')}>‹ Leads</button>
        <div style={{ fontSize: 12.5, color: C.faint }}>{lead.lead_code}</div>
      </div>
      {toast && <div style={{ marginBottom: 12, padding: '9px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>{toast}</div>}
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Left card */}
        <div style={{ ...card, width: 300, padding: 16, flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{leadName(lead)}</div>
          <div style={{ margin: '6px 0 10px' }}><StagePill stage={lead.stage} label={stageLabel(meta, lead.stage)} /></div>
          <a href={`tel:+91${lead.phone}`} onClick={() => markCall({})} style={{ display: 'block', fontSize: 15, fontWeight: 700, color: C.brand, textDecoration: 'none' }}>📞 +91 {lead.phone}</a>
          {lead.dnd_call ? <div style={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 700, marginTop: 4 }}>Do not call</div> : null}
          <div style={{ marginTop: 10 }}>
            {row('Source', <SourceTag source={lead.source} />)}
            {lead.branch_interested && row('Store asked', lead.branch_interested)}
            {lead.pincode && row('Pincode', lead.pincode)}
            {lead.city && row('City', lead.city)}
            {lead.legacy_stage && row('LSQ stage', lead.legacy_stage)}
            {row('Store', lead.store_name || lead.store_id)}
            {row('Owner', lead.owner_staff || 'Unassigned')}
            {lead.visit_day && row('Visit day', fmtDate(lead.visit_day))}
            {row('Created', fmtDateTime(lead.created_at))}
            {row('Expected', fmtINR(lead.expected_order_value))}
            {lead.order_name && row('Order', `${lead.order_name} · ${fmtINR(lead.order_value)}`)}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
            <button onClick={() => { setCallTaskId(null); setDrawer('call'); }} style={quick}>📞 Log call</button>
            <button onClick={() => setTab('notes')} style={quick}>📝 Note</button>
            <button onClick={() => setDrawer('task')} style={quick}>📅 Task</button>
            <button onClick={() => setDrawer('stage')} style={quick}>🏷️ Change stage</button>
            <button onClick={() => setDrawer('link')} style={quick}>🛒 Link order</button>
            {perms.canReassign && <button onClick={() => setDrawer('reassign')} style={quick}>↪️ Reassign</button>}
            {perms.canRequestReassign && <button onClick={() => setDrawer('request')} style={quick}>↪️ Request move</button>}
            {perms.canDelete && <button onClick={async () => {
              if (!window.confirm(`Delete ${lead.lead_code}? It is hidden from every list (kept in the audit trail).`)) return;
              try { await api.delete(`/leads/${lead.id}`); notify(); navigate('/leads'); } catch (e) { flash(errOf(e, 'Could not delete')); }
            }} style={{ ...quick, color: '#b91c1c' }}>🗑️ Delete</button>}
          </div>
        </div>

        {/* Centre */}
        <div style={{ ...card, flex: 1, minWidth: 380 }}>
          <div style={{ display: 'flex', gap: 4, padding: '10px 12px', borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
            {[['activity', 'Activity History'], ['tasks', `Tasks (${openTasks.length})`], ['details', 'Lead Details'], ['remarks', 'Remarks']].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} style={pill(tab === k)}>{l}</button>
            ))}
          </div>
          <div style={{ padding: 14 }}>
            {tab === 'activity' && (activities.length === 0 ? <div style={{ color: C.faint, fontSize: 13 }}>No activity yet.</div> : activities.map(a => {
              const [icon, label] = ACT[a.activity_type] || ['•', a.activity_type];
              const d = a.details || {};
              const disp = (meta.dispositions.find(x => x.code === a.disposition) || {}).label;
              const text = [
                a.activity_type === 'CALL_LOGGED' && `${a.call_status === 'connected' ? 'Connected' : 'Not connected'} — ${disp || a.disposition}`,
                a.activity_type === 'STAGE_CHANGED' && `${stageLabel(meta, a.old_stage)} → ${stageLabel(meta, a.new_stage)}`,
                (a.activity_type === 'TASK_CREATED' || a.activity_type === 'TASK_COMPLETED' || a.activity_type === 'TASK_CANCELLED') && `${taskLabel(meta, d.taskType)}${d.scheduledAt ? ` · ${fmtDateTime(d.scheduledAt)}` : ''}${d.outcome ? ` · ${OUTCOMES[d.outcome] || d.outcome}` : ''}${d.auto ? ' · auto' : ''}`,
                a.activity_type === 'ORDER_LINKED' && `${d.orderName} · ${fmtINR(d.orderValue)}${d.otherStore ? ` · placed at ${d.orderStore}${d.viaReview ? ' (approved in review)' : ' (flag for ABM review)'}` : ''}${d.auto ? ' · matched by phone' : ''}`,
                a.activity_type === 'REASSIGN_REQUESTED' && `to ${d.toStore}`,
                a.activity_type === 'LEAD_REASSIGNED' && `${d.fromStore} → ${d.toStore}`,
                a.activity_type === 'LEAD_UPDATED' && (d.changed || []).join(', '),
                a.activity_type === 'RE_ENQUIRY' && `via ${d.via}`
              ].filter(Boolean).join('');
              return (
                <div key={a.id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #f4ede4' }}>
                  <div style={{ fontSize: 16, width: 22, textAlign: 'center' }}>{icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: C.text }}><b>{label}</b>{text ? ` · ${text}` : ''}</div>
                    {a.remarks && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2, whiteSpace: 'pre-wrap' }}>{a.remarks}</div>}
                    <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>{fmtDateTime(a.created_at)} · {a.staff_username || 'system'}</div>
                  </div>
                </div>
              );
            }))}
            {tab === 'tasks' && (
              <div>
                <button onClick={() => setDrawer('task')} style={{ ...btn('primary'), marginBottom: 10 }}>＋ Add task</button>
                {tasks.length === 0 && <div style={{ color: C.faint, fontSize: 13 }}>No tasks yet.</div>}
                {tasks.map(t => {
                  const overdue = t.status === 'open' && new Date(t.scheduled_at) < new Date();
                  return (
                    <div key={t.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #f4ede4', opacity: t.status === 'open' ? 1 : 0.6, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{t.subject}</div>
                        <div style={{ fontSize: 11.5, color: overdue ? '#b91c1c' : C.muted, fontWeight: overdue ? 700 : 400 }}>
                          {fmtDateTime(t.scheduled_at)}{overdue ? ' · Overdue' : ''} · {t.owner_staff || 'store'}
                          {t.status !== 'open' && ` · ${t.status === 'done' ? (OUTCOMES[t.outcome] || 'Done') : 'Cancelled'}`}
                        </div>
                        {t.description && <div style={{ fontSize: 12, color: C.muted }}>{t.description}</div>}
                      </div>
                      {t.status === 'open' && (<>
                        <button onClick={() => { setCallTaskId(t.id); setDrawer('call'); }} style={{ ...btn('secondary'), padding: '5px 9px', fontSize: 12 }}>Log call</button>
                        <button onClick={() => setComplete(t)} style={{ ...btn('primary'), padding: '5px 9px', fontSize: 12 }}>✓ Complete</button>
                        <button onClick={async () => { if (window.confirm('Cancel this task?')) { try { await api.post(`/leads/tasks/${t.id}/cancel`, {}); done('Task cancelled'); } catch (e) { flash(errOf(e, 'Could not cancel')); } } }} style={{ ...btn('secondary'), padding: '5px 9px', fontSize: 12 }}>Cancel</button>
                      </>)}
                    </div>
                  );
                })}
              </div>
            )}
            {tab === 'details' && <LeadDetailsForm lead={lead} perms={perms} onSaved={(n) => done(n ? `Saved (${n} change${n > 1 ? 's' : ''})` : 'No changes')} />}
            {tab === 'remarks' && <RemarksForm lead={lead} onSaved={() => done('Remarks saved')} />}
            {tab === 'notes' && <div style={{ fontSize: 12.5, color: C.muted }}>Add notes in the panel on the right.</div>}
          </div>
        </div>

        {/* Notes rail */}
        <NotesRail lead={lead} notes={notes} onSaved={() => done('Note added')} highlight={tab === 'notes'} />
      </div>

      {drawer === 'call' && <CallFormDrawer lead={lead} meta={meta} completeTaskId={callTaskId} onClose={() => { setDrawer(null); setCallTaskId(null); }}
        onSaved={(r) => { done(r.message || 'Call saved'); if (r.promptLinkOrder) setTimeout(() => setDrawer('link'), 200); }} />}
      {drawer === 'task' && <TaskDrawer lead={lead} meta={meta} onClose={() => setDrawer(null)} onSaved={() => done('Task added')} />}
      {drawer === 'stage' && <StageDrawer lead={lead} meta={meta} onClose={() => setDrawer(null)} onSaved={() => done('Stage changed')} />}
      {drawer === 'link' && <LinkOrderDrawer lead={lead} onClose={() => setDrawer(null)} onSaved={(m) => done(m)} />}
      {drawer === 'reassign' && <ReassignDrawer lead={lead} onClose={() => setDrawer(null)} onSaved={() => { notify(); navigate('/leads'); }} />}
      {drawer === 'request' && <RequestMoveDrawer lead={lead} onClose={() => setDrawer(null)} onSaved={() => done('Request sent to your manager')} />}
      {complete && <CompleteTaskModal task={complete} onClose={() => setComplete(null)} onSaved={() => { setComplete(null); done('Task completed'); }} />}
    </div>
  );
}

function NotesRail({ lead, notes, onSaved, highlight }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setSaving(true); setError('');
    try { await api.post(`/leads/${lead.id}/notes`, { note: text }); setText(''); onSaved(); } catch (e) { setError(errOf(e, 'Could not save the note')); }
    setSaving(false);
  };
  return (
    <div style={{ ...card, width: 280, padding: 14, flexShrink: 0, borderColor: highlight ? C.brand : C.border }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 8 }}>Notes</div>
      <textarea value={text} onChange={e => setText(e.target.value)} maxLength={2000} rows={3} placeholder="Add a note…" style={{ ...inputStyle, resize: 'vertical' }} autoFocus={highlight} />
      <button onClick={save} disabled={saving || !text.trim()} style={{ ...btn('primary', saving || !text.trim()), marginTop: 6, width: '100%' }}>＋ Add</button>
      <ErrorBox text={error} />
      <div style={{ marginTop: 10 }}>
        {notes.length === 0 && <div style={{ fontSize: 12, color: C.faint }}>No notes yet.</div>}
        {notes.map(n => (
          <div key={n.id} style={{ padding: '8px 0', borderBottom: '1px solid #f4ede4' }}>
            <div style={{ fontSize: 12.5, color: C.text, whiteSpace: 'pre-wrap' }}>{n.note}</div>
            <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>{n.author} · {fmtDateTime(n.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LeadDetailsForm({ lead, perms, onSaved }) {
  const init = useMemo(() => ({
    phone: lead.phone, firstName: lead.first_name || '', lastName: lead.last_name || '', email: lead.email || '',
    dob: lead.dob ? String(lead.dob).slice(0, 10) : '', address: lead.address || '', pincode: lead.pincode || '', city: lead.city || '', state: lead.state || '',
    expectedOrderValue: lead.expected_order_value ?? '', ownerStaff: lead.owner_staff || '',
    firstResponseAt: lead.first_response_at ? toLocalInput(lead.first_response_at) : ''
  }), [lead]);
  const [f, setF] = useState(init);
  const [products, setProducts] = useState(lead.products_interested || []);
  const [dnd, setDnd] = useState({ call: !!lead.dnd_call, email: !!lead.dnd_email, sms: !!lead.dnd_sms, track: !!lead.dnd_track });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    setSaving(true); setError('');
    const body = { ...f, productsInterested: products, dnd, firstResponseAt: f.firstResponseAt ? fromLocalInput(f.firstResponseAt) : undefined };
    if (!perms.canEditPhone) delete body.phone;
    try { const r = await api.put(`/leads/${lead.id}`, body); onSaved((r.data.changed || []).length); } catch (e) { setError(errOf(e, 'Could not save')); }
    setSaving(false);
  };
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 12px' };
  return (
    <div>
      <div style={grid}>
        <div><label style={labelStyle}>Mobile number</label><input value={f.phone} onChange={set('phone')} disabled={!perms.canEditPhone} title={perms.canEditPhone ? '' : 'Only a manager can change the phone number'} style={{ ...inputStyle, background: perms.canEditPhone ? '#fff' : '#f9fafb' }} /></div>
        <div><label style={labelStyle}>First name</label><input value={f.firstName} onChange={set('firstName')} style={inputStyle} /></div>
        <div><label style={labelStyle}>Last name</label><input value={f.lastName} onChange={set('lastName')} style={inputStyle} /></div>
        <div><label style={labelStyle}>Email</label><input value={f.email} onChange={set('email')} style={inputStyle} /></div>
        <div><label style={labelStyle}>Date of birth</label><input type="date" value={f.dob} onChange={set('dob')} style={inputStyle} /></div>
        <div><label style={labelStyle}>Owner</label><OwnerSelect storeId={lead.store_id} value={f.ownerStaff} onChange={v => setF(s => ({ ...s, ownerStaff: v }))} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Address</label><input value={f.address} onChange={set('address')} style={inputStyle} /></div>
        <div><label style={labelStyle}>Pincode</label><input value={f.pincode} onChange={set('pincode')} maxLength={6} style={inputStyle} /></div>
        <div><label style={labelStyle}>City</label><input value={f.city} onChange={set('city')} style={inputStyle} /></div>
        <div><label style={labelStyle}>State</label><input value={f.state} onChange={set('state')} style={inputStyle} /></div>
        <div><label style={labelStyle}>Expected order total (₹)</label><input value={f.expectedOrderValue} onChange={set('expectedOrderValue')} style={inputStyle} /></div>
        <div><label style={labelStyle}>First response</label><input type="datetime-local" value={f.firstResponseAt} onChange={set('firstResponseAt')} style={inputStyle} /></div>
      </div>
      <label style={labelStyle}>Products interested</label>
      <ProductPicker value={products} onChange={setProducts} />
      <label style={labelStyle}>Do not contact</label>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13 }}>
        {[['call', 'Call'], ['email', 'Email'], ['sms', 'SMS'], ['track', 'Track']].map(([k, l]) => (
          <label key={k} style={{ display: 'flex', gap: 5, alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={dnd[k]} onChange={e => setDnd(d => ({ ...d, [k]: e.target.checked }))} style={{ accentColor: C.brand }} />{l}</label>
        ))}
      </div>
      <ErrorBox text={error} />
      <div style={{ marginTop: 14 }}><button onClick={save} disabled={saving} style={btn('primary', saving)}>{saving ? 'Saving…' : 'Save details'}</button></div>
    </div>
  );
}

function RemarksForm({ lead, onSaved }) {
  const [text, setText] = useState(lead.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setSaving(true); setError('');
    try { await api.put(`/leads/${lead.id}`, { notes: text }); onSaved(); } catch (e) { setError(errOf(e, 'Could not save')); }
    setSaving(false);
  };
  return (
    <div>
      <textarea value={text} onChange={e => setText(e.target.value)} maxLength={2000} rows={8} style={{ ...inputStyle, resize: 'vertical' }} />
      <div style={{ fontSize: 11, color: C.faint, textAlign: 'right' }}>{text.length}/2000</div>
      <ErrorBox text={error} />
      <button onClick={save} disabled={saving} style={{ ...btn('primary', saving), marginTop: 8 }}>{saving ? 'Saving…' : 'Save remarks'}</button>
    </div>
  );
}

function StageDrawer({ lead, meta, onClose, onSaved }) {
  const [stage, setStage] = useState(lead.stage);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setSaving(true); setError('');
    try { await api.post(`/leads/${lead.id}/stage`, { stage, reason }); onSaved(); } catch (e) { setError(errOf(e, 'Could not change the stage')); }
    setSaving(false);
  };
  return (
    <Drawer title="Change stage" onClose={onClose} width={400}
      footer={<><button onClick={onClose} style={btn('secondary')}>Cancel</button><button onClick={save} disabled={saving || stage === lead.stage} style={btn('primary', saving || stage === lead.stage)}>Save</button></>}>
      <label style={labelStyle}>Stage</label>
      {[['open', 'Open'], ['closed', 'Closed – lost']].map(([g, gl]) => (
        <div key={g}>
          <div style={{ fontSize: 11, color: C.faint, margin: '8px 0 2px', fontWeight: 700 }}>{gl}</div>
          {meta.stages.filter(s => s.stage_group === g).map(s => (
            <label key={s.code} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" checked={stage === s.code} onChange={() => setStage(s.code)} style={{ accentColor: C.brand }} /><StagePill stage={s.code} label={s.label} />
            </label>
          ))}
        </div>
      ))}
      <div style={{ fontSize: 11.5, color: C.faint, marginTop: 8 }}>"Customer" is set by linking the order.</div>
      <label style={labelStyle}>Reason</label>
      <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
      <ErrorBox text={error} />
    </Drawer>
  );
}

function LinkOrderDrawer({ lead, onClose, onSaved }) {
  const [ref, setRef] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await api.post(`/leads/${lead.id}/link-order`, { orderRef: ref });
      onSaved(`Order ${r.data.order.name} linked — lead is now a Customer${r.data.order.otherStore ? ' (order from another store: flagged for ABM review)' : ''}`);
    } catch (e) { setError(errOf(e, 'Could not link the order')); }
    setSaving(false);
  };
  return (
    <Drawer title="Link order" onClose={onClose} width={400}
      footer={<><button onClick={onClose} style={btn('secondary')}>Cancel</button><button onClick={save} disabled={saving || !ref.trim()} style={btn('primary', saving || !ref.trim())}>{saving ? 'Linking…' : 'Link order'}</button></>}>
      <label style={labelStyle}>Order number</label>
      <input value={ref} onChange={e => setRef(e.target.value)} placeholder="e.g. POS-BANG-53441 or invoice number" style={inputStyle} autoFocus />
      <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>The lead moves to <b>Customer</b>, takes the order value, and its Deal closure task is closed.</div>
      <ErrorBox text={error} />
    </Drawer>
  );
}

function ReassignDrawer({ lead, onClose, onSaved }) {
  const [stores, setStores] = useState([]);
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { api.get('/locations').then(r => setStores((r.data.locations || []).filter(l => String(l.id) !== String(lead.store_id)))).catch(() => {}); }, [lead.store_id]);
  const save = async () => {
    setSaving(true); setError('');
    try { await api.post(`/leads/${lead.id}/reassign`, { storeId: target, reason }); onSaved(); } catch (e) { setError(errOf(e, 'Could not reassign')); }
    setSaving(false);
  };
  return (
    <Drawer title="Reassign to another store" onClose={onClose} width={420}
      footer={<><button onClick={onClose} style={btn('secondary')}>Cancel</button><button onClick={save} disabled={saving || !target} style={btn('primary', saving || !target)}>Reassign</button></>}>
      <label style={labelStyle}>Store</label>
      <select value={target} onChange={e => setTarget(e.target.value)} style={inputStyle}>
        <option value="">Choose…</option>
        {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <label style={labelStyle}>Reason</label>
      <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
      <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>The owner is cleared and open tasks move with the lead. Logged on the lead.</div>
      <ErrorBox text={error} />
    </Drawer>
  );
}

// Store login: ask a manager / HO to move the lead (lands in their Review queue).
function RequestMoveDrawer({ lead, onClose, onSaved }) {
  const [stores, setStores] = useState([]);
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { api.get('/locations').then(r => setStores((r.data.locations || []).filter(l => String(l.id) !== String(lead.store_id)))).catch(() => {}); }, [lead.store_id]);
  const save = async () => {
    setSaving(true); setError('');
    try { await api.post(`/leads/${lead.id}/reassign-request`, { storeId: target || undefined, reason }); onSaved(); } catch (e) { setError(errOf(e, 'Could not send the request')); }
    setSaving(false);
  };
  return (
    <Drawer title="Request to move this lead" onClose={onClose} width={420}
      footer={<><button onClick={onClose} style={btn('secondary')}>Cancel</button><button onClick={save} disabled={saving || !reason.trim()} style={btn('primary', saving || !reason.trim())}>Send request</button></>}>
      <label style={labelStyle}>Move to (if you know)</label>
      <select value={target} onChange={e => setTarget(e.target.value)} style={inputStyle}>
        <option value="">Let the manager decide</option>
        {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <label style={labelStyle}>Why?</label>
      <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="e.g. Customer lives near Worli and wants to visit there" style={{ ...inputStyle, resize: 'vertical' }} />
      <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Your manager / ABM approves it in the Review queue. Until then the lead stays with your store.</div>
      <ErrorBox text={error} />
    </Drawer>
  );
}
